import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "@semo/icons";

import { cn } from "../utils/cn";

const buttonVariants = cva(
  "relative cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive active:scale-[0.97]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-pressed",
        destructive: "bg-destructive text-white hover:bg-destructive/90",
        "outline-destructive":
          "border border-destructive text-destructive bg-background hover:bg-destructive/5",
        outline:
          "border bg-background text-foreground hover:bg-accent hover:text-accent-foreground",
        "outline-primary":
          "border border-primary text-primary-text bg-background hover:bg-primary/5 hover:text-primary-text",
        "soft-primary":
          "bg-primary/10 text-primary-text border-transparent hover:bg-primary/20",
        "soft-destructive":
          "bg-destructive/10 text-destructive border-transparent hover:bg-destructive/20",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary-text underline-offset-4 hover:underline",
        gradient:
          "bg-gradient-to-r from-primary to-secondary text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/25",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-6",
        xl: "h-14 rounded-xl px-8 text-lg font-semibold",
        icon: "size-9 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export interface ButtonProps extends React.ComponentPropsWithoutRef<"button"> {
  /** 버튼의 시각적 스타일을 결정합니다. */
  variant?: ButtonVariantProps["variant"];
  /** 버튼의 크기를 결정합니다. */
  size?: ButtonVariantProps["size"];
  /** Radix UI의 Slot을 사용하여 자식 컴포넌트에게 스타일을 위임합니다. */
  asChild?: boolean;
  /** 로딩 상태 여부를 결정합니다. */
  isLoading?: boolean;
  /** 버튼 왼쪽에 표시할 아이콘입니다. */
  leftIcon?: React.ReactNode;
  /** 버튼 오른쪽에 표시할 아이콘입니다. */
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    if (asChild) {
      const isMultipleChildren = React.Children.count(children) > 1;
      const content = isMultipleChildren ? (
        <span className="inline-flex items-center gap-2">{children}</span>
      ) : (
        children
      );

      return (
        <Comp
          ref={ref}
          disabled={disabled ?? isLoading}
          className={cn(buttonVariants({ variant, size, className }))}
          {...props}
        >
          {content}
        </Comp>
      );
    }

    return (
      <Comp
        ref={ref}
        disabled={disabled ?? isLoading}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : null}

        <span
          className={cn(
            "inline-flex items-center gap-2 transition-opacity",
            isLoading ? "opacity-0" : "opacity-100",
          )}
        >
          {leftIcon}
          {children}
          {rightIcon}
        </span>
      </Comp>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
